import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DateInputIT } from "../../components/common/DateTimeIT";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { Plus, Search, Download, Copy, Building2 } from "lucide-react";
import { toast } from "sonner";
import {
  StatsRow, StatCard, Section, SectionLabel, Alert,
} from "../../components/result/Primitives";
import { BackupStatusCard } from "../../components/BackupStatusCard";

const PLAN_BADGE = {
  internal:     { bg: "bg-subtle",        color: "text-text-secondary", label: "Internal" },
  professional: { bg: "bg-accent-subtle", color: "text-accent-strong", label: "Professional" },
  business:     { bg: "bg-accent-subtle", color: "text-accent-strong", label: "Business" },
  enterprise:   { bg: "bg-brand/10",      color: "text-text-primary",  label: "Enterprise" },
};
const STATUS_BADGE = {
  active:    { bg: "bg-success-subtle", color: "text-success-strong", label: "Attiva" },
  suspended: { bg: "bg-danger-subtle",  color: "text-danger-strong",  label: "Sospesa" },
  trial:     { bg: "bg-warning-subtle", color: "text-warning-strong", label: "Trial" },
};

const Badge = ({ kind, value }) => {
  const map = kind === "plan" ? PLAN_BADGE : STATUS_BADGE;
  const def = map[value] || { bg: "bg-subtle", color: "text-text-secondary", label: value };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${def.bg} ${def.color}`}>
      {def.label}
    </span>
  );
};

const QuotaCell = ({ used, limit }) => {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const over = used > limit;
  const barColor = over ? "bg-accent" : pct >= 80 ? "bg-warning" : "bg-success";
  return (
    <div className="min-w-[120px]">
      <div className="text-xs text-text-secondary tabular-nums">{used} / {limit >= 999999 ? "∞" : limit}</div>
      <div className="mt-1 h-1 bg-subtle rounded-full overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const fmtEur = (cents) => `€ ${(Number(cents || 0) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SuperAdminOrganizations() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [plan, setPlan] = useState("all");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null);

  const [failed, setFailed] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (plan !== "all") params.plan_id = plan;
      if (search) params.search = search;
      const { data } = await api.get("/superadmin/organizations", { params });
      setOrgs(data);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status, plan]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aggregate KPIs
  const kpis = useMemo(() => {
    const active = orgs.filter((o) => o.status === "active");
    const mrr = active.reduce((s, o) => s + (o.plan?.monthly_fee_cents || 0), 0);
    const revenue = orgs.reduce((s, o) => s + (o.revenue_month_cents || 0), 0);
    const cost = orgs.reduce((s, o) => s + (o.cost_month_cents || 0), 0);
    const margin = revenue - cost;
    const avgQuota = active.length === 0 ? 0 : Math.round(
      active.reduce((s, o) => {
        const lim = o.monthly_quota_limit || 0;
        return s + (lim > 0 ? Math.min(100, (o.monthly_quota_used || 0) / lim * 100) : 0);
      }, 0) / active.length
    );
    return { count: active.length, mrr, margin, avgQuota };
  }, [orgs]);

  const onSearchSubmit = (e) => { e.preventDefault(); load(); };

  const exportCsv = () => {
    const rows = [
      ["ID", "Nome", "Piano", "Status", "Utenti", "Quota usata", "Quota limite", "Ricavi mese (€)", "Costo OpenAPI (€)", "Margine (€)"],
      ...orgs.map((o) => [
        o.id, o.name, o.plan_id, o.status,
        o.user_count, o.monthly_quota_used, o.monthly_quota_limit,
        (o.revenue_month_cents/100).toFixed(2),
        (o.cost_month_cents/100).toFixed(2),
        (o.margin_month_cents/100).toFixed(2),
      ]),
    ];
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "organizzazioni.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-medium tracking-widest uppercase text-text-muted mb-2">Wattline · Super admin</div>
          <h1 className="text-3xl font-semibold text-text-primary tracking-tight">Organizzazioni</h1>
          <p className="text-base text-text-secondary mt-2">Gestisci i clienti della piattaforma</p>
        </div>
        <Button
          data-testid="sa-create-org-btn"
          onClick={() => setCreateOpen(true)}
          className="bg-accent hover:bg-accent-strong text-white rounded-md h-10 px-4"
        >
          <Plus size={15} className="mr-1.5" /> Nuova organizzazione
        </Button>
      </div>

      {/* Stato backup (visibilità dentro Wattline: mai solo via email) */}
      <BackupStatusCard />

      {/* KPI */}
      <StatsRow columns={4}>
        <StatCard label="Org. attive" value={kpis.count} detail="Totali" testId="sa-kpi-orgs" />
        <StatCard label="MRR" value={fmtEur(kpis.mrr)} detail="Ricorrente" testId="sa-kpi-mrr" />
        <StatCard label="Margine mese" value={fmtEur(kpis.margin)} detail="Ricavi − Costi OpenAPI" testId="sa-kpi-margin" />
        <StatCard label="Quota media" value={`${kpis.avgQuota}%`} detail="Utilizzo medio org" testId="sa-kpi-quota" />
      </StatsRow>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <form onSubmit={onSearchSubmit} className="flex-1 max-w-md relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            data-testid="sa-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, ragione sociale o P.IVA…"
            className="pl-9 h-10 rounded-md border-border bg-surface"
          />
        </form>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 h-10 rounded-md border-border bg-surface" data-testid="sa-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="active">Attive</SelectItem>
            <SelectItem value="suspended">Sospese</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
          </SelectContent>
        </Select>
        <Select value={plan} onValueChange={setPlan}>
          <SelectTrigger className="w-44 h-10 rounded-md border-border bg-surface" data-testid="sa-plan-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i piani</SelectItem>
            <SelectItem value="internal">Internal</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="business">Business</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={exportCsv} className="h-10 rounded-md border-border" data-testid="sa-export-csv">
          <Download size={14} className="mr-1.5" /> Esporta CSV
        </Button>
      </div>

      {/* Table */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden" data-testid="sa-orgs-table">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-text-secondary text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Nome</th>
              <th className="text-left px-5 py-3 font-medium">Piano</th>
              <th className="text-left px-5 py-3 font-medium">Status</th>
              <th className="text-left px-5 py-3 font-medium">Utenti</th>
              <th className="text-left px-5 py-3 font-medium">Quota</th>
              <th className="text-right px-5 py-3 font-medium">Ricavi mese</th>
              <th className="text-right px-5 py-3 font-medium">Margine</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-text-muted">Caricamento…</td></tr>
            ) : failed ? (
              <tr><td colSpan={7}><LoadErrorBox what="le organizzazioni" onRetry={load} testId="sa-orgs-error" /></td></tr>
            ) : orgs.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-text-muted">Nessuna organizzazione</td></tr>
            ) : orgs.map((o) => (
              <tr
                key={o.id}
                className="border-t border-border hover:bg-subtle transition-colors cursor-pointer"
                onClick={() => navigate(`/superadmin/organizations/${o.id}`)}
                data-testid={`sa-org-row-${o.id}`}
              >
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-md bg-subtle flex items-center justify-center flex-shrink-0">
                      <Building2 size={14} className="text-text-secondary" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-text-primary truncate">{o.name}</div>
                      <div className="text-xs text-text-muted truncate">{o.legal_name || o.id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3.5"><Badge kind="plan" value={o.plan_id} /></td>
                <td className="px-5 py-3.5"><Badge kind="status" value={o.status} /></td>
                <td className="px-5 py-3.5 text-text-secondary tabular-nums">{o.user_count || 0}</td>
                <td className="px-5 py-3.5"><QuotaCell used={o.monthly_quota_used} limit={o.monthly_quota_limit} /></td>
                <td className="px-5 py-3.5 text-right tabular-nums text-text-primary font-medium">{fmtEur(o.revenue_month_cents)}</td>
                <td className="px-5 py-3.5 text-right tabular-nums">
                  <span className={o.margin_month_cents >= 0 ? "text-success-strong" : "text-danger-strong"}>
                    {fmtEur(o.margin_month_cents)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateOrgDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(creds) => { setCreatedCreds(creds); load(); }} />
      <CredsDialog creds={createdCreds} onClose={() => setCreatedCreds(null)} />
    </div>
  );
}

// ============================================================
// Create dialog
// ============================================================
const CreateOrgDialog = ({ open, onClose, onCreated }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: "", legal_name: "", vat_number: "", billing_email: "",
    plan_id: "professional",
    contract_start_date: today,
    contract_end_date: "",
    setup_fee_paid: false,
    notes: "",
    street: "", city: "", province: "", cap: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        name: form.name, legal_name: form.legal_name, vat_number: form.vat_number,
        billing_email: form.billing_email,
        billing_address: (form.street || form.city) ? {
          street: form.street, city: form.city, province: form.province, cap: form.cap, country: "Italia",
        } : null,
        plan_id: form.plan_id,
        contract_start_date: form.contract_start_date,
        contract_end_date: form.contract_end_date || null,
        setup_fee_paid: !!form.setup_fee_paid,
        notes: form.notes || null,
      };
      const { data } = await api.post("/superadmin/organizations", payload);
      toast.success("Organizzazione creata");
      onCreated({
        email: data.admin_user.email,
        password: data.admin_user.temporary_password,
        org_name: data.organization.name,
      });
      onClose();
      // reset
      setForm({ ...form, name: "", legal_name: "", vat_number: "", billing_email: "", notes: "", street: "", city: "", province: "", cap: "" });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Errore");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="wl-modal wl-modal--lg overflow-y-auto"
        style={{maxHeight: "90vh", padding: 0, gap: 0, border: "none", boxShadow: "var(--wl-shadow-elevated)"}}
        data-testid="sa-create-org-dialog"
      >
        <div className="wl-modal__header">
          <div className="wl-modal__title-block">
            <DialogTitle className="wl-modal__title">Nuova organizzazione</DialogTitle>
            <DialogDescription className="wl-modal__desc">Crea l&apos;org e l&apos;utente admin associato.</DialogDescription>
          </div>
        </div>
        <form onSubmit={submit} className="px-6 pb-2 space-y-4">
          <div className="wl-field-row wl-field-row--2col">
            <FormField label="Nome commerciale" required value={form.name} onChange={(v) => upd("name", v)} testId="org-name" placeholder="es. Acme Energy" />
            <FormField label="Ragione sociale" required value={form.legal_name} onChange={(v) => upd("legal_name", v)} testId="org-legal" placeholder="es. Acme Energy S.r.l." />
            <FormField label="P.IVA" required value={form.vat_number} onChange={(v) => upd("vat_number", v)} testId="org-vat" placeholder="es. IT12345678901" />
            <FormField label="Billing email" type="email" required value={form.billing_email} onChange={(v) => upd("billing_email", v)} testId="org-email" placeholder="es. billing@acme.it" />
          </div>
          <SectionLabel>Indirizzo di fatturazione (opzionale)</SectionLabel>
          <div className="wl-field-row wl-field-row--2col">
            <FormField label="Indirizzo" value={form.street} onChange={(v) => upd("street", v)} testId="org-street" placeholder="es. Via Roma 12" />
            <FormField label="Città" value={form.city} onChange={(v) => upd("city", v)} testId="org-city" placeholder="es. Milano" />
            <FormField label="Provincia" value={form.province} onChange={(v) => upd("province", v)} testId="org-province" placeholder="es. MI" />
            <FormField label="CAP" value={form.cap} onChange={(v) => upd("cap", v)} testId="org-cap" placeholder="es. 20100" />
          </div>
          <SectionLabel>Contratto</SectionLabel>
          <div className="wl-field-row wl-field-row--2col">
            <div className="wl-field">
              <label htmlFor="org-plan" className="wl-label wl-label--required">Piano</label>
              <select
                id="org-plan"
                value={form.plan_id}
                onChange={(e) => upd("plan_id", e.target.value)}
                data-testid="org-plan"
                className="wl-select"
              >
                <option value="professional">Professional (€800/mese)</option>
                <option value="business">Business (€1.500/mese)</option>
                <option value="enterprise">Enterprise (negoziabile)</option>
              </select>
            </div>
            <FormField label="Data inizio contratto" required type="date" value={form.contract_start_date} onChange={(v) => upd("contract_start_date", v)} testId="org-start" />
            <FormField label="Data fine contratto" type="date" value={form.contract_end_date} onChange={(v) => upd("contract_end_date", v)} testId="org-end" />
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" data-testid="org-setup-paid" checked={form.setup_fee_paid} onChange={(e) => upd("setup_fee_paid", e.target.checked)} />
              <span className="text-sm text-text-primary">Setup fee già pagato</span>
            </label>
          </div>
          <div className="wl-field">
            <label htmlFor="org-notes" className="wl-label">Note commerciali</label>
            <textarea
              id="org-notes"
              data-testid="org-notes"
              value={form.notes}
              onChange={(e) => upd("notes", e.target.value)}
              rows={3}
              className="wl-textarea"
              placeholder="es. Cliente acquisito tramite partnership X"
            />
          </div>
          {error && <div data-testid="org-error" className="wl-error-msg">{error}</div>}
          <div className="wl-modal__actions">
            <button type="button" onClick={onClose} disabled={submitting} className="wl-btn wl-btn--ghost">Annulla</button>
            <button type="submit" disabled={submitting} className="wl-btn wl-btn--primary" data-testid="org-submit">
              {submitting ? "Creazione…" : "Crea organizzazione"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const FormField = ({ label, value, onChange, type = "text", required, testId, placeholder }) => (
  <div className="wl-field">
    <label htmlFor={testId} className={`wl-label ${required ? "wl-label--required" : ""}`}>{label}</label>
    {type === "date" ? (
      <DateInputIT id={testId} value={value} onChange={onChange} testId={testId} placeholder={placeholder || "gg/mm/aaaa"} />
    ) : (
    <input
      id={testId}
      type={type}
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      className="wl-input"
    />
    )}
  </div>
);

const CredsDialog = ({ creds, onClose }) => {
  if (!creds) return null;
  const copy = () => {
    navigator.clipboard.writeText(`Email: ${creds.email}\nPassword: ${creds.password}`);
    toast.success("Credenziali copiate");
  };
  return (
    <Dialog open={!!creds} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="wl-modal"
        style={{padding: 0, gap: 0, border: "none", boxShadow: "var(--wl-shadow-elevated)"}}
        data-testid="creds-dialog"
      >
        <div className="wl-modal__header">
          <div className="wl-modal__title-block">
            <DialogTitle className="wl-modal__title">Organizzazione creata</DialogTitle>
            <DialogDescription className="wl-modal__desc">Salva queste credenziali. Non saranno più visibili.</DialogDescription>
          </div>
        </div>
        <div className="px-6 pb-2 space-y-3">
          <Alert variant="warning">
            <strong>Una sola volta!</strong> Queste credenziali non sono recuperabili. Copia e invia all&apos;admin di {creds.org_name}.
          </Alert>
          <div className="bg-page rounded-md p-4 space-y-2 font-mono text-sm border border-border" data-testid="creds-block">
            <div><span className="text-text-secondary">Email:</span> {creds.email}</div>
            <div><span className="text-text-secondary">Password:</span> {creds.password}</div>
          </div>
        </div>
        <div className="wl-modal__actions">
          <button type="button" onClick={copy} className="wl-btn wl-btn--ghost" data-testid="creds-copy"><Copy size={14} /> Copia credenziali</button>
          <button type="button" onClick={onClose} className="wl-btn wl-btn--primary" data-testid="creds-close">Ho salvato</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
